// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Anchor, Group, type GroupProps, Modal, Stack, Text, type TextProps } from '@mantine/core';
import { formatAppointmentRangeInTimezone, getResolvedTimezone, getTimezoneAbbreviation } from '@polarissleep/shared';
import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { formatAppointmentDateShort, formatAppointmentTime } from '../../utils/appointment-formatting';

interface AppointmentTimeWithTimezoneProps {
  start: string | undefined;
  end: string | undefined;
  groupProps?: GroupProps;
  textProps?: Omit<TextProps, 'children'>;
  /**
   * Render the date on a line above the time. Opt-in, for lists that span more
   * than one day — a same-day list reads better without the repetition.
   */
  showDate?: boolean;
}

export function AppointmentTimeWithTimezone({
  start,
  end,
  groupProps,
  textProps,
  showDate = false,
}: AppointmentTimeWithTimezoneProps): JSX.Element {
  const [opened, setOpened] = useState(false);
  const viewerTimezone = useMemo(() => getResolvedTimezone(), []);
  const timezoneLabel = getTimezoneAbbreviation(start, viewerTimezone) || viewerTimezone;
  const localRange = formatAppointmentRangeInTimezone(start, end, viewerTimezone);
  const utcRange = formatAppointmentRangeInTimezone(start, end, 'UTC');

  const timeRow = (
    <Group gap={4} wrap="wrap" {...groupProps}>
      <Text {...textProps}>{formatAppointmentTime(start, end)}</Text>
      {start && end && (
        <Anchor
          component="button"
          type="button"
          size={textProps?.size ?? 'sm'}
          onClick={() => setOpened(true)}
          aria-label="View appointment time zone details"
        >
          {timezoneLabel}
        </Anchor>
      )}
    </Group>
  );

  return (
    <>
      {showDate ? (
        <Stack gap={0}>
          <Text fw={500} {...textProps}>
            {formatAppointmentDateShort(start)}
          </Text>
          {timeRow}
        </Stack>
      ) : (
        timeRow
      )}

      <Modal opened={opened} onClose={() => setOpened(false)} title="Time zone details" centered>
        <Stack gap="md">
          <div>
            <Text fw={600} size="sm">
              Your device time zone
            </Text>
            <Text size="sm" c="dimmed">
              {viewerTimezone}
            </Text>
          </div>
          <div>
            <Text fw={600} size="sm">
              Appointment for you
            </Text>
            <Text size="sm">{localRange}</Text>
          </div>
          <div>
            <Text fw={600} size="sm">
              UTC reference
            </Text>
            <Text size="sm">{utcRange}</Text>
          </div>
          <Text size="sm" c="dimmed">
            Appointment times adapt automatically to each viewer&apos;s local device time zone.
          </Text>
        </Stack>
      </Modal>
    </>
  );
}
